'use client';

import { useState, useEffect, useMemo } from 'react';
import './styles/@theme';
import { DYBPageIntroduction } from './dyb-page-introduction';
import { DYBPage } from './dyb-page';
import { DYB_INTERSTITIAL_MAX_TIMES_SHOWN, getPagesData } from './show-dyb-interstitial-utils';
import { usePreloadAssets } from '@/hooks/use-preload-assets/use-preload-assets';
import {
    setDYBCounterInUserPreferences,
    setDYBV2CounterInUserPreferences
} from '@/logic/dyb-interstatial/dyb-interstatial-helper';
import type { Tutorial } from './types';
import type { SiteWideJackpotSlicePrimary } from '@/types/zones/slices/site-wide-jackpot-slice';
import { classes } from '@/utils/classes/classes';

type DYBInterstitialProps = {
    onClose: () => void;
    onCloseWithoutTracking: () => void;
    popupRef?: React.RefObject<HTMLDivElement | null>;
    fade?: boolean;
    onAnimationEnd?: (event: React.AnimationEvent) => void;
    tutorial: Tutorial | null | undefined;
    logoUrl?: string;
    swjCategory?: SiteWideJackpotSlicePrimary['category'];
    isSWJTutorialV2?: boolean;
};

export const DYBInterstitial = ({
    onClose,
    onCloseWithoutTracking,
    popupRef,
    fade,
    onAnimationEnd,
    tutorial,
    logoUrl,
    swjCategory,
    isSWJTutorialV2 = false
}: DYBInterstitialProps) => {
    const [currentPage, setCurrentPage] = useState(0);
    const setNextPage = () => setCurrentPage((current) => current + 1);
    const setPrevPage = () => setCurrentPage((current) => current - 1);
    const pages = useMemo(() => getPagesData(tutorial), [tutorial]);
    const totalNumberOfSteps = pages.length;
    const pageImageUrls = pages.map((p) => p.IMAGE);
    usePreloadAssets(pageImageUrls);

    const hasNoTutorialPages = pages.length <= 1;

    useEffect(() => {
        if (hasNoTutorialPages) {
            onCloseWithoutTracking();
        }
    }, [hasNoTutorialPages, onCloseWithoutTracking]);

    if (hasNoTutorialPages) {
        return null;
    }

    const markCounterAtMax = () => {
        if (isSWJTutorialV2) {
            setDYBV2CounterInUserPreferences(DYB_INTERSTITIAL_MAX_TIMES_SHOWN);
        } else {
            setDYBCounterInUserPreferences(DYB_INTERSTITIAL_MAX_TIMES_SHOWN);
        }
    };

    const handleCloseFromNonIntroPage = () => {
        markCounterAtMax();
        onCloseWithoutTracking();
    };

    const pickPage = () => {
        if (currentPage === 0) {
            if (!pages[0]) {
                return null;
            }
            return (
                <DYBPageIntroduction
                    pageData={pages[0]}
                    setNextPage={setNextPage}
                    onClose={onClose}
                    logoUrl={logoUrl}
                />
            );
        }

        if (!pages[currentPage]) {
            return null;
        }

        return (
            <DYBPage
                pageData={pages[currentPage]}
                currentPage={currentPage}
                setNextPage={setNextPage}
                setPrevPage={setPrevPage}
                totalNumberOfSteps={totalNumberOfSteps - 1}
                onClose={handleCloseFromNonIntroPage}
                swjCategory={swjCategory}
            />
        );
    };

    const handleOverlayClick = () => {
        if (currentPage > 0) {
            markCounterAtMax();
        }
    };

    return (
        <div className="dyb-interstitial-structure-wrapper">
            <div className="dyb-interstitial-background-overlay" onClick={handleOverlayClick} />
            <div
                ref={popupRef}
                className={classes('dyb-interstitial-content-wrapper', fade && 'fade-out')}
                onAnimationEnd={onAnimationEnd}>
                {pickPage()}
            </div>
        </div>
    );
};
